import React, { useState, useEffect } from "react";
import { createGrammarSubTopic, updateGrammarSubTopic } from "./adminGrammarService";
import "../../styles/content.css";

const emptySubSection = () => ({ heading: "", content: "", example: "", sortOrder: 1 });
const emptySection = () => ({ heading: "", content: "", example: "", sortOrder: 1, subSections: [] });

const GrammarSubTopicForm = ({ subTopic, grammarTopicId, onSaved, onCancel }) => {
  const isEdit = !!subTopic;
  const [form, setForm] = useState({ title: "", youtubeLink: "", sortOrder: "" });
  const [sections, setSections] = useState([emptySection()]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (subTopic) {
      console.log("[GrammarSubTopicForm] Populating form for edit, id:", subTopic.id);
      setForm({ title: subTopic.title || "", youtubeLink: subTopic.youtubeLink || "", sortOrder: subTopic.sortOrder ?? "" });
      if (subTopic.sections?.length) {
        setSections(subTopic.sections.map((s) => ({
          heading: s.heading || "",
          content: s.content || "",
          example: s.example || "",
          sortOrder: s.sortOrder || 1,
          subSections: (s.subSections || []).map((ss) => ({
            heading: ss.heading || "",
            content: ss.content || "",
            example: ss.example || "",
            sortOrder: ss.sortOrder || 1,
          })),
        })));
      }
    }
  }, [subTopic]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSectionChange = (idx, field, value) => {
    const updated = [...sections];
    updated[idx] = { ...updated[idx], [field]: value };
    setSections(updated);
  };

  const addSection = () => setSections([...sections, { ...emptySection(), sortOrder: sections.length + 1 }]);

  const removeSection = (idx) => {
    if (sections.length <= 1) return;
    setSections(sections.filter((_, i) => i !== idx).map((s, i) => ({ ...s, sortOrder: i + 1 })));
  };

  // Sub-section handlers
  const handleSubSectionChange = (secIdx, subIdx, field, value) => {
    const updated = [...sections];
    const subs = [...updated[secIdx].subSections];
    subs[subIdx] = { ...subs[subIdx], [field]: value };
    updated[secIdx] = { ...updated[secIdx], subSections: subs };
    setSections(updated);
  };

  const addSubSection = (secIdx) => {
    const updated = [...sections];
    const subs = [...updated[secIdx].subSections, { ...emptySubSection(), sortOrder: updated[secIdx].subSections.length + 1 }];
    updated[secIdx] = { ...updated[secIdx], subSections: subs };
    setSections(updated);
  };

  const removeSubSection = (secIdx, subIdx) => {
    const updated = [...sections];
    const subs = updated[secIdx].subSections.filter((_, i) => i !== subIdx).map((ss, i) => ({ ...ss, sortOrder: i + 1 }));
    updated[secIdx] = { ...updated[secIdx], subSections: subs };
    setSections(updated);
  };

  const handleSubmit = async () => {
    if (!form.title) {
      console.warn("[GrammarSubTopicForm] Validation failed: missing title");
      alert("Title is required");
      return;
    }
    if (sections.some((s) => !s.heading || !s.content)) {
      console.warn("[GrammarSubTopicForm] Validation failed: incomplete sections");
      alert("Each section needs a heading and content");
      return;
    }

    const payload = {
      title: form.title,
      grammarTopicId: grammarTopicId,
      sortOrder: form.sortOrder !== "" ? Number(form.sortOrder) : null,
      ...(form.youtubeLink && { youtubeLink: form.youtubeLink }),
      sections: sections.map((s, i) => ({
        heading: s.heading,
        content: s.content,
        example: s.example,
        sortOrder: i + 1,
        subSections: s.subSections.length > 0
          ? s.subSections.map((ss, j) => ({ heading: ss.heading, content: ss.content, example: ss.example, sortOrder: j + 1 }))
          : null,
      })),
    };

    console.log("[GrammarSubTopicForm] Submitting", isEdit ? "update" : "create", "payload:", payload);
    setLoading(true);
    const res = isEdit
      ? await updateGrammarSubTopic(subTopic.id, payload)
      : await createGrammarSubTopic(payload);
    if (res.status === 200 || res.status === 201) {
      console.log("[GrammarSubTopicForm] Submit successful:", res.status);
      onSaved();
    } else {
      console.error("[GrammarSubTopicForm] Submit failed:", res.status, res.message);
      alert(res.message || "Operation failed");
    }
    setLoading(false);
  };

  const subSectionStyle = {
    border: "1px dashed #ccc", borderRadius: 6, padding: 10, marginTop: 8, background: "#fff",
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ width: 640, maxHeight: "90vh", overflowY: "auto" }}>
        <div className="form-card" style={{ margin: 0, boxShadow: "none" }}>
          <h2>{isEdit ? "Edit" : "Create"} Sub Topic</h2>

          <div className="form-group">
            <label>Title *</label>
            <input name="title" placeholder="e.g. Nouns" value={form.title} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label>Sort Order</label>
            <input name="sortOrder" type="number" placeholder="e.g. 1" value={form.sortOrder} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label>YouTube Link (optional)</label>
            <input name="youtubeLink" placeholder="https://youtube.com/watch?v=..." value={form.youtubeLink} onChange={handleChange} />
          </div>

          <hr style={{ margin: "16px 0" }} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0 }}>Sections ({sections.length})</h3>
            <button type="button" onClick={addSection} style={{ fontSize: 13 }}>+ Add Section</button>
          </div>

          {sections.map((sec, idx) => (
            <div key={idx} style={{ border: "1px solid #eee", borderRadius: 8, padding: 12, marginTop: 12, background: "#fafafa" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <strong>Section {idx + 1}</strong>
                {sections.length > 1 && (
                  <button type="button" onClick={() => removeSection(idx)} style={{ color: "red", background: "none", border: "none", cursor: "pointer", fontSize: 13 }}>
                    ✕ Remove
                  </button>
                )}
              </div>
              <div className="form-group">
                <label>Heading *</label>
                <input placeholder="Section heading" value={sec.heading} onChange={(e) => handleSectionChange(idx, "heading", e.target.value)} />
              </div>
              <div className="form-group">
                <label>Content *</label>
                <textarea placeholder="Section content..." value={sec.content} onChange={(e) => handleSectionChange(idx, "content", e.target.value)} rows={3} />
              </div>
              <div className="form-group">
                <label>Example</label>
                <textarea placeholder="e.g. dog, London, happiness" value={sec.example} onChange={(e) => handleSectionChange(idx, "example", e.target.value)} rows={2} />
              </div>

              {/* Sub-Sections */}
              <div style={{ marginTop: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <strong style={{ fontSize: 13, color: "#555" }}>Sub-Sections ({sec.subSections.length})</strong>
                  <button type="button" onClick={() => addSubSection(idx)} style={{ fontSize: 12, background: "none", border: "none", color: "#019095", cursor: "pointer", fontWeight: 600 }}>
                    + Add Sub-Section
                  </button>
                </div>
                {sec.subSections.map((ss, subIdx) => (
                  <div key={subIdx} style={subSectionStyle}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#666" }}>Sub-Section {subIdx + 1}</span>
                      <button type="button" onClick={() => removeSubSection(idx, subIdx)} style={{ color: "red", background: "none", border: "none", cursor: "pointer", fontSize: 12 }}>
                        ✕
                      </button>
                    </div>
                    <div className="form-group">
                      <label style={{ fontSize: 12 }}>Heading</label>
                      <input placeholder="Sub-section heading" value={ss.heading} onChange={(e) => handleSubSectionChange(idx, subIdx, "heading", e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label style={{ fontSize: 12 }}>Content</label>
                      <textarea placeholder="Sub-section content..." value={ss.content} onChange={(e) => handleSubSectionChange(idx, subIdx, "content", e.target.value)} rows={2} />
                    </div>
                    <div className="form-group">
                      <label style={{ fontSize: 12 }}>Example</label>
                      <textarea placeholder="Sub-section example" value={ss.example} onChange={(e) => handleSubSectionChange(idx, subIdx, "example", e.target.value)} rows={2} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="form-actions" style={{ marginTop: 16 }}>
            <button onClick={handleSubmit} disabled={loading}>
              {loading ? "Saving..." : isEdit ? "Update" : "Create"}
            </button>
            <button onClick={onCancel} disabled={loading}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrammarSubTopicForm;
